import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { MappingSpec, loadSpec, sameTable, specToMapping } from '../../services/mappingSpec';
import type { MappingItem } from '../workflow/panels/PropertyPanel/TransformMappingModal';

interface Props {
  projectId: string;
  /** 노드의 타깃 테이블 — 같은 타깃의 매핑을 맨 위에, 표시해서 보여 준다 */
  target?: { schema?: string; table?: string };
  current?: string;
  onPick: (r: { mappingId: string; version: number; items: MappingItem[] }) => void;
  onClose: () => void;
}

/**
 * 변환및적재 노드 → 「설계서에서 가져오기」. 매핑 설계서 최신 판의 매핑 하나를 골라 그 판의 매핑 사본을 노드에 넣는다.
 * 만들 수 없는 규칙(소스 없음·코드그룹 없음 등)이 있으면 가져오지 않는다 — 설계서에서 고친 뒤 다시.
 */
const MappingSpecPicker: React.FC<Props> = ({ projectId, target, current, onPick, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [spec, setSpec] = useState<MappingSpec | null>(null);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(current || '');

  useEffect(() => {
    loadSpec(projectId).then(setSpec).catch((e) => setError(e?.message || String(e)));
  }, [projectId]);

  const rows = useMemo(() => {
    const list = (spec?.tables || []).filter((x) => x.mappingId.trim()).map((x) => ({
      ...x, match: !!target?.table && sameTable(x.tgtTable, target.schema || '', target.table),
    }));
    return [...list.filter((x) => x.match), ...list.filter((x) => !x.match)];
  }, [spec, target?.schema, target?.table]);
  useEffect(() => { if (!sel && rows.length) setSel(rows[0].mappingId); }, [rows, sel]);

  const built = useMemo(() => (spec && sel ? specToMapping(spec, sel) : null), [spec, sel]);
  const chosen = rows.find((x) => x.mappingId === sel);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[760px] max-w-[95vw] max-h-[85vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('mapping:picker.title')}</div>
            <div className="text-xs text-gray-500">{spec ? (spec.version ? t('mapping:picker.latest', { v: spec.version }) : t('mapping:neverSaved')) : t('mapping:loading')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>
        {error ? <div className="p-4 text-xs text-red-600">{error}</div> : (
          <div className="flex-1 min-h-0 flex gap-3 p-3">
            <div className="w-[48%] overflow-auto border border-gray-200 rounded">
              {!rows.length && spec && <div className="p-3 text-xs text-gray-400">{t('mapping:picker.none')}</div>}
              {rows.map((x) => (
                <button key={x.mappingId} type="button" onClick={() => setSel(x.mappingId)}
                  className={classNames('w-full text-left px-2 py-1.5 border-b border-gray-100 text-xs', sel === x.mappingId ? 'bg-blue-50' : 'hover:bg-gray-50')}>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-gray-800">{x.mappingId}</span>
                    {x.match && <span className="px-1 rounded bg-green-100 text-green-700 text-[0.625rem]">{t('mapping:picker.sameTarget')}</span>}
                  </div>
                  <div className="text-gray-500 truncate">{x.srcTable || '?'} → {x.tgtTable || '?'}</div>
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-auto text-xs">
              {chosen && built && (
                <>
                  {target?.table && !chosen.match && <div className="mb-2 text-amber-700">{t('mapping:picker.otherTarget', { table: chosen.tgtTable, node: `${target.schema ? `${target.schema}.` : ''}${target.table}` })}</div>}
                  {built.problems.length > 0 && (
                    <ul className="mb-2 text-red-600 space-y-0.5">
                      {built.problems.map((p, i) => <li key={i}>✕ {t(`mapping:issue.${p.code}`, { col: p.target, group: '' })}</li>)}
                    </ul>
                  )}
                  <div className="text-gray-500 mb-1">{t('mapping:picker.preview', { n: built.items.length })}</div>
                  <ul className="font-mono text-[0.6875rem] text-gray-700 space-y-0.5">
                    {built.items.map((m, i) => <li key={i} className="break-all">{m.target} ← {m.expr || m.source}</li>)}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}
        <div className="px-4 py-2 border-t border-gray-200 flex justify-end gap-2">
          <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            disabled={!spec || !built || !built.items.length || built.problems.length > 0}
            onClick={() => spec && built && onPick({ mappingId: sel, version: spec.version, items: built.items })}>
            {t('mapping:picker.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MappingSpecPicker;
